import { twMerge } from '@/lib/classname/twMerge'
import { BaseSkeleton } from '@/ui/skeleton/BaseSkeleton'
import { type VariantProps, cva } from 'class-variance-authority'

const AvatarCva = cva(
  'border border-transparent relative flex size-[1.75rem] shrink-0 items-center justify-center rounded-full group-hover/avatar:bg-brand-1-700',
  {
    variants: {
      active: {
        true: 'bg-brand-1-700',
        false: 'bg-brand-2-800',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
)

export type AvatarVariantProps = RequiredProps<VariantProps<typeof AvatarCva>, 'active'>

export type AvatarProps = WithClassName<
  WithTestId<
    AvatarVariantProps & {
      /** The name that will determine the letter inside the component. Required if no icon is provided. */
      name?: string
      /** An optional icon to display instead of the initials. */
      icon?: React.ReactNode
      id?: string
      /** Used internally for accessibility. Provide one if the context requires it. */
      'aria-label'?: string
    }
  >
>

export function Avatar({
  active,
  name,
  icon,
  className,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  ...props
}: AvatarProps): React.ReactElement {
  const initial = name ? name.charAt(0).toUpperCase() : ''

  return (
    <div
      {...props}
      id={id}
      className={twMerge(className, AvatarCva({ active }))}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      role={!ariaLabel ? 'presentation' : 'img'}
      aria-hidden={!ariaLabel ? 'true' : undefined}
    >
      {icon ? (
        <span className="flex items-center justify-center text-white">{icon}</span>
      ) : (
        <span
          aria-hidden="true"
          className="flex flex-col items-center justify-center text-center text-sm font-bold text-white"
        >
          {initial}
        </span>
      )}
    </div>
  )
}

export function AvatarSkeleton({ className }: { className?: string }): React.ReactElement {
  return (
    <BaseSkeleton className={twMerge(className, 'size-[1.75rem] shrink-0 grow-0 rounded-full')} />
  )
}
